import React, { useEffect, useState } from "react";
import { Field, Select, TextArea, Form } from "../commercial/components/ui";
export function applyCallCorrectionResponse(current, data) {
  if (!data.audit || !data.state) return current;
  const audit = data.audit,
    corrected = data.state.results.find(
      (row) => String(row.id) === String(audit.resultId),
    );
  const changedIds = new Set(
    audit.taskChanges.map((change) => String(change.id)),
  );
  const correctedTasks = new Map(
    data.state.tasks
      .filter((task) => changedIds.has(String(task.id)))
      .map((task) => [String(task.id), task]),
  );
  const serverClient = data.state.clients.find(
    (client) => String(client.id) === String(audit.clientId),
  );
  const fields = [
    "history",
    "lastCallResult",
    "lastCallNote",
    "lastCallAt",
    "lastCallCorrectionAt",
    ...(changedIds.size ? ["nextAction", "next_action_date"] : []),
  ];
  const results = current.results.map((row) =>
    String(row.id) === String(audit.resultId) ||
    (!row.id &&
      String(row.clientId) === String(audit.clientId) &&
      row.createdAt === audit.originalCreatedAt)
      ? corrected
      : row,
  );
  return {
    ...current,
    results,
    tasks: current.tasks.map(
      (task) => correctedTasks.get(String(task.id)) || task,
    ),
    clients: current.clients.map((client) =>
      String(client.id) === String(audit.clientId)
        ? {
            ...client,
            ...Object.fromEntries(
              fields
                .filter((field) => serverClient?.[field] !== undefined)
                .map((field) => [field, serverClient[field]]),
            ),
          }
        : client,
    ),
    events: current.events.some((event) => event.id === audit.id)
      ? current.events
      : [...current.events, audit],
  };
}
export default function CorrectLastCall({
  api,
  client,
  options,
  onCorrected,
  onClose,
}) {
  const [loaded, setLoaded] = useState(null),
    [draft, setDraft] = useState({ label: "", notes: "", due: "" }),
    [validated, setValidated] = useState([]),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [operation, setOperation] = useState("correct"),
    [cancelReason, setCancelReason] = useState("");
  const clientId = String(
    client.client_id || client.establishment_id || client.id,
  );
  const load = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get(
        `commercial/calls/${encodeURIComponent(clientId)}/last`,
      );
      setLoaded(data);
      setDraft({
        label: data.call.label,
        notes: data.call.notes || "",
        due: data.call.due || "",
      });
      setValidated([]);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Impossible de charger le dernier appel.",
      );
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load();
  }, [api, clientId]);
  const submit = async () => {
    if (busy || !loaded) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(
        `commercial/calls/${encodeURIComponent(clientId)}/correct-last`,
        {
          ...draft,
          ...(operation === "cancel" ? { operation, cancelReason } : {}),
          expectedVersion: loaded.expectedVersion,
          validatedTaskIds: validated,
        },
      );
      onCorrected(data);
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message || "La correction n’a pas été enregistrée.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <p className="font-semibold">{client.name}</p>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {busy && !loaded && <p role="status">Chargement du dernier appel…</p>}
      {loaded && (
        <>
          <p className="text-sm">
            Appel du{" "}
            {loaded.call.createdAt
              ? new Date(loaded.call.createdAt).toLocaleString("fr-FR")
              : "date non renseignée"}
            . Le compte rendu initial reste dans l’historique.
          </p>
          {loaded.call.corrections?.length > 0 && (
            <details>
              <summary>
                Historique des corrections ({loaded.call.corrections.length})
              </summary>
              {loaded.call.corrections.map((entry) => (
                <article
                  key={entry.id}
                  className="mb-3 rounded-lg border p-3 text-sm"
                >
                  <p>
                    {new Date(entry.createdAt).toLocaleString("fr-FR")} ·{" "}
                    {entry.actorName || entry.actorId}
                  </p>
                  <p>
                    Résultat : {entry.before.label} → {entry.after.label}
                  </p>
                  <p>Note initiale : {entry.before.notes || "Aucune"}</p>
                  <p>Note corrigée : {entry.after.notes || "Aucune"}</p>
                  <p>
                    Relance : {entry.before.due || "Aucune"} →{" "}
                    {entry.after.due || "Aucune"}
                  </p>
                </article>
              ))}
            </details>
          )}
          <Form onSubmit={submit}>
            <Select
              label="Action sur le dernier appel"
              value={operation}
              onChange={(e) => setOperation(e.target.value)}
            >
              <option value="correct">Corriger le compte rendu</option>
              <option value="cancel">
                Annuler cet appel enregistré par erreur
              </option>
            </Select>
            {operation === "cancel" && (
              <>
                <p>
                  Le compte rendu initial reste dans l’historique. Les besoins,
                  devis, oppositions et engagements déjà créés ne sont pas
                  annulés par cette action ; ils doivent être examinés
                  séparément.
                </p>
                <TextArea
                  label="Motif d’annulation"
                  value={cancelReason}
                  maxLength={1000}
                  required
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </>
            )}
            <Select
              label="Résultat de l’appel"
              value={draft.label}
              onChange={(event) =>
                setDraft({ ...draft, label: event.target.value })
              }
            >
              {[...new Set([loaded.call.label, ...options])].map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </Select>
            <TextArea
              label="Note de l’appel"
              value={draft.notes}
              maxLength={10000}
              onChange={(event) =>
                setDraft({ ...draft, notes: event.target.value })
              }
            />
            <Field
              label="Date de relance"
              type="date"
              value={draft.due}
              onChange={(event) =>
                setDraft({ ...draft, due: event.target.value })
              }
            />
            {loaded.tasks.length > 0 ? (
              <fieldset className="rounded-lg border p-3">
                <legend>Tâches automatiques liées</legend>
                {loaded.tasks.map((task) => (
                  <div key={task.id} className="mb-2 text-sm">
                    <p>{task.title}</p>
                    {task.requiresValidation || !task.canValidate ? (
                      <>
                        <p className="text-amber-800">
                          {task.reason || "Validation responsable nécessaire"}.
                          Cette tâche reste inchangée sans validation.
                        </p>
                        {task.canValidate && (
                          <label>
                            <input
                              type="checkbox"
                              checked={validated.includes(task.id)}
                              onChange={(event) =>
                                setValidated((ids) =>
                                  event.target.checked
                                    ? [...ids, task.id]
                                    : ids.filter((id) => id !== task.id),
                                )
                              }
                            />{" "}
                            J’autorise la mise à jour de cette tâche.
                          </label>
                        )}
                      </>
                    ) : (
                      <p>
                        Cette tâche sera mise à jour en conservant son
                        identifiant.
                      </p>
                    )}
                  </div>
                ))}
              </fieldset>
            ) : (
              <p className="text-sm">
                Aucune tâche automatique liée à cet appel.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button type="submit" className="primary" disabled={busy}>
                {busy
                  ? "Enregistrement…"
                  : operation === "cancel"
                    ? "Confirmer l’annulation"
                    : "Enregistrer la correction"}
              </button>
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={onClose}
              >
                Annuler
              </button>
            </div>
          </Form>
        </>
      )}
      {error && (
        <button
          type="button"
          className="secondary"
          disabled={busy}
          onClick={load}
        >
          Recharger le dernier appel
        </button>
      )}
    </div>
  );
}
